import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SettingsIcon from "@mui/icons-material/Settings";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { SystemLinearMeter } from "../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { BADGE, FONT_SIZE, LETTER_SPACING } from "../../../theme/tokens";
import type { PbjCategoryScore, PbjPrediction } from "../../../utils/models";
import { bufferPointsCost } from "./bandBuffer.utils";
import { CategoryExpandContent, type CategoryImprovement } from "./CategoryExpandContent";
import { CensusDialog } from "./CensusDialog";
import { brandBorder, brandTint, meterFill, ROW_OPEN_BG, TABLE_HEAD_BG, TRACK_BG } from "./colors";
import { DAYS_PER_WEEK_FULL, DAYS_PER_WEEK_WEEKEND, WEEKEND_CATEGORY_ID } from "./cumulativeScoring.utils";
import { HppdExplainTooltipContent } from "./HppdExplainTooltipContent";
import { ManualTurnoverDialog } from "./ManualTurnoverDialog";
import { NATIONAL_DEFAULTS } from "./nationalDefaults";
import type { PbjDraftControls } from "./pbjDraftControls";

// The inline meter column is a visual nicety, not load-bearing — the score numbers carry the
// data. Its show/hide is keyed on the CARD's own width (the breakdown establishes itself as a
// container) rather than the viewport: at the 2-col threshold the viewport is wide (~1120px) but
// each column is only ~400px, and the meter (+ its padding) then pushes the table past its
// container so the Points header/values clip. Below this width the meter drops out and the metric
// + points cells get the room back; at comfortable widths (desktop 2-col ~560px, stacked
// full-width) it returns.
const BREAKDOWN_CONTAINER = "pbj-breakdown";
const BREAKDOWN_METER_MIN = 450;
const BREAKDOWN_METER_QUERY = `@container ${BREAKDOWN_CONTAINER} (min-width: ${BREAKDOWN_METER_MIN}px)`;
// Once the meter drops out (narrow card), also tighten the metric + points cell horizontal
// padding so the points value ("10 / 100") gets its full column back and stays on one line
// instead of wrapping/clipping against the card edge.
const BREAKDOWN_NARROW_QUERY = `@container ${BREAKDOWN_CONTAINER} (max-width: ${BREAKDOWN_METER_MIN - 1}px)`;

// Per-HPPD-category national benchmark: the CMS case-mix national-average adjusted HPPD the
// facility's ADJUSTED value is scored against (`adjusted = reported × natCaseMix / facilityCM`,
// so the national-average facility's adjusted equals its case-mix HRD). Sourced from the
// national-average snapshot in nationalDefaults.ts (mirror of the BE constants). Turnover/Admin
// have no national benchmark in that snapshot, so they show no benchmark line — honest, not faked.
const HPPD_NATIONAL_BENCHMARK: Record<string, number> = {
	"1": NATIONAL_DEFAULTS.natCaseMixTotalHrd,
	"2": NATIONAL_DEFAULTS.natCaseMixRnHrd,
	"3": NATIONAL_DEFAULTS.natCaseMixWeekendHrd,
};

// Turnover (total/RN) and Admin Turnover support manual entry / N/A; HPPD measures do not.
const isEditable = (category: PbjCategoryScore): boolean => category.kind === "turnover" || category.kind === "admin";

type Props = {
	prediction: PbjPrediction;
	// Live config draft + persist/recompute callbacks (one grouped prop shared with the
	// hero + recommendations sidebar). Saving a manual turnover override both applies the
	// edit and triggers a recalculation, so the user doesn't have to also click the
	// page-level Calculate button.
	draftControls: PbjDraftControls;
};

// Each band is its own row with a flat point value. The xlsx scoring ladder has 10
// bands per HPPD/turnover category (two per star tier) and 3 for admin; rendering
// them flat matches the BE IFS formulas 1:1, so the user can see exactly which
// threshold maps to which score instead of inferring it from a collapsed range.
const CategoryTableRow: FC<{
	category: PbjCategoryScore;
	census: number;
	targetStarRating: number;
	// The department's safety-margin band buffer (0–100), straight from the config the
	// prediction was scored with — 0 = exact CMS scoring.
	bandRangeBufferPct: number;
	onEdit: (category: PbjCategoryScore) => void;
}> = ({ category, census, targetStarRating: _t, bandRangeBufferPct, onEdit }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const tint = brandTint(theme);
	const [open, setOpen] = useState(false);
	// N/A measures are excluded from the rating; show "N/A" instead of points and skip the
	// "next goal" math (there's nothing to improve toward when the measure doesn't count).
	const naExcluded = !category.available;
	// Invalid means the category stays in the total but is force-scored at the lowest band.
	// N/A wins when both are set, so we gate the Invalid affordances on `!naExcluded`.
	const isInvalid = (category.invalid ?? false) && !naExcluded;
	// Where a turnover value came from. Missing on older payloads → treat as "internal".
	const source = category.source ?? "internal";
	// CASPER-sourced value: the badge/formula-note affordances only show when the measure
	// still has a value (not N/A-excluded, not Invalid) — otherwise the source is moot.
	const isCasper = source === "casper" && !naExcluded && !isInvalid;
	// CASPER covers the facility but this field is blank — arrives available=false, so it
	// falls into the naExcluded path; we only override its caption to be CASPER-specific.
	const isCasperMissing = source === "casper_missing";
	const editable = isEditable(category);
	// A category can be available (counts toward the rating) yet have no points scale
	// (maxScore 0) — e.g. a measure with an empty band ladder. There's nothing to score
	// against, so we render a muted "—" and no meter rather than a misleading empty bar.
	const hasScoreScale = category.maxScore > 0;
	// The meter is a plain neutral "how full" bar (points/max) with no good/bad grading.
	const meterColor = meterFill(theme);

	// Improvement maths: how far to the next sub-band in the metric's natural unit, plus the
	// hours-per-day translation for HPPD categories. The card always tracks the immediate next
	// sub-band so the diff matches the next visible row in the flat threshold table.
	const improvement = useMemo<CategoryImprovement | null>(() => {
		if (naExcluded) return null;
		if (category.nextBandStar === null || category.nextBandMinValue === null || category.nextBandPoints === null) return null;
		const target = category.nextBandMinValue;
		const diff = category.kind === "turnover" ? category.rawValue - target : target - category.rawValue;
		// Hours-per-WEEK to close the gap. HPPD diff × census = hours per applicable day.
		// Weekend HPPD lands only on Sat+Sun → × 2 days/week; Total + RN HPPD
		// apply every day → × 7 days/week.
		const daysPerWeek = category.id === WEEKEND_CATEGORY_ID ? DAYS_PER_WEEK_WEEKEND : DAYS_PER_WEEK_FULL;
		return {
			diff: Math.abs(diff),
			pointsGain: category.nextBandPoints - category.score,
			hoursNeeded: category.kind === "hppd" && census > 0 ? Math.ceil(Math.abs(diff) * census * daysPerWeek) : null,
			nextPoints: category.nextBandPoints,
			target,
		};
	}, [category, census, naExcluded]);

	// Points the safety-margin band buffer costs this measure. Only meaningful for a measure
	// that's actually being scored on its value — N/A is excluded from the rating and Invalid
	// is force-scored at the lowest band, so neither gap belongs to the buffer.
	const bufferCost = bandRangeBufferPct > 0 && !naExcluded && !isInvalid ? bufferPointsCost(category) : 0;

	const description = t(`pbj_model.scoring.description.${category.id}`);

	// Caption priority: CASPER-missing note > N/A note > Invalid note > the formatted metric
	// value. A CASPER-missing measure is technically N/A-excluded, but reads more helpfully as
	// "Missing information in CASPER" than the generic excluded note. For a present CASPER value
	// the caption stays the % value — the "From CASPER" badge conveys the source.
	let caption: string = category.value;
	if (isCasperMissing) caption = t("pbj_model.scoring.casper.missing");
	else if (naExcluded) caption = t("pbj_model.scoring.na.excluded_note");
	else if (isInvalid) caption = t("pbj_model.scoring.invalid.note");

	// National benchmark diagnostic (HPPD categories only): the facility's real adjusted HPPD
	// against the CMS national-average adjusted HPPD for this measure. Rendered as a second
	// caption line ("national 3.84") with a plain above/below arrow — factual context, no color
	// judgment. Only shown for a scored, non-N/A HPPD measure with a benchmark; turnover/admin
	// have no national default.
	const nationalBenchmark = category.kind === "hppd" ? HPPD_NATIONAL_BENCHMARK[category.id] : undefined;
	const showBenchmark = !naExcluded && hasScoreScale && nationalBenchmark !== undefined;
	// The ↑/↓ arrow still conveys above/below the national average; it's rendered in a neutral
	// muted tone (not success/warning) so the row carries no good/bad color coding.
	const atOrAboveNational = showBenchmark && nationalBenchmark !== undefined && category.rawValue >= nationalBenchmark;

	return (
		<>
			<TableRow
				hover
				onClick={() => setOpen(!open)}
				sx={{
					cursor: "pointer",
					// Title, points and chevron sit on the top line; the meter (3rd cell) centers vertically
					// against the multi-line metric cell. nth-of-type(3) out-specifies the general top rule.
					"& > .MuiTableCell-root": { borderBottom: "unset", verticalAlign: "top" },
					"& > .MuiTableCell-root:nth-of-type(3)": { verticalAlign: "middle" },
					bgcolor: open ? ROW_OPEN_BG : "transparent",
				}}
				data-testid={`pbj-model-category-${category.id}`}
			>
				<TableCell sx={{ py: 0.5, pl: 1, pr: 0, width: 36 }}>
					<IconButton size="small" aria-label={t(open ? "pbj_model.category.collapse" : "pbj_model.category.expand")}>
						{open ? <KeyboardArrowUpIcon sx={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
					</IconButton>
				</TableCell>
				<TableCell sx={{ py: 1, pl: 0.5, pr: 2, [BREAKDOWN_NARROW_QUERY]: { pr: 1 } }}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{category.title}
						</Typography>
						{/* The numbers behind the measure — staff, hours, census, case mix — on hover. Only the
						    HPPD measures carry a breakdown; a future-only quarter withholds it rather than
						    describing a window with no elapsed days, and then no icon renders at all.
						    Wrapped so a click lands on the icon instead of toggling the row's expand panel. */}
						{category.explain && (
							<Box
								component="span"
								onClick={(event) => event.stopPropagation()}
								sx={{ display: "inline-flex", alignItems: "center" }}
								data-testid={`pbj-model-category-${category.id}-explain-trigger`}
							>
								<InfoIconWithTooltip
									title={<HppdExplainTooltipContent categoryId={category.id} categoryTitle={category.title} explain={category.explain} />}
									iconSx={{ fontSize: 14, color: "text.secondary" }}
									dataTestId={`pbj-model-category-${category.id}-explain-icon`}
								/>
							</Box>
						)}
						{naExcluded && (
							<Chip
								label={t("pbj_model.scoring.na.badge")}
								size="small"
								sx={{ height: BADGE.TAG_CHIP_HEIGHT_SM, fontSize: FONT_SIZE.LABEL_XXS, bgcolor: "action.hover", color: "text.secondary", fontWeight: 700 }}
								data-testid={`pbj-model-category-${category.id}-na`}
							/>
						)}
						{isInvalid && (
							<Chip
								label={t("pbj_model.scoring.invalid.badge")}
								size="small"
								sx={{ height: BADGE.TAG_CHIP_HEIGHT_SM, fontSize: FONT_SIZE.LABEL_XXS, bgcolor: "action.hover", color: "text.secondary", fontWeight: 700 }}
								data-testid={`pbj-model-category-${category.id}-invalid`}
							/>
						)}
						{category.manualOverride && !naExcluded && !isInvalid && (
							<Chip
								label={t("pbj_model.scoring.manual.badge")}
								size="small"
								sx={{ height: BADGE.TAG_CHIP_HEIGHT_SM, fontSize: FONT_SIZE.LABEL_XXS, bgcolor: tint, color: accent, fontWeight: 700 }}
								data-testid={`pbj-model-category-${category.id}-manual`}
							/>
						)}
						{isCasper && (
							<Chip
								label={t("pbj_model.scoring.casper.badge")}
								size="small"
								sx={{ height: BADGE.TAG_CHIP_HEIGHT_SM, fontSize: FONT_SIZE.LABEL_XXS, bgcolor: tint, color: accent, fontWeight: 700 }}
								data-testid={`pbj-model-category-${category.id}-casper`}
							/>
						)}
						{/* The buffer is the only reason this measure scores below what its CMS row says —
						    without saying so on the collapsed row, a correct score reads as a bug. */}
						{bufferCost > 0 && (
							<MetaChip
								label={t("pbj_model.scoring.buffer.row_badge")}
								icon={<ShieldOutlinedIcon />}
								tone="info"
								tooltip={t("pbj_model.scoring.buffer.row_tooltip", { pct: bandRangeBufferPct, score: category.score, cmsScore: category.score + bufferCost })}
								dataTestId={`pbj-model-category-${category.id}-buffer`}
							/>
						)}
					</Stack>
					{/* Plain-language description of the measure — always visible (previously only in
					    the expand panel) so the breakdown reads self-explanatory. */}
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.25, lineHeight: 1.4 }}
						data-testid={`pbj-model-category-${category.id}-description`}
					>
						{description}
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontVariantNumeric: "tabular-nums" }}>
						{caption}
					</Typography>
					{/* This measure's real value vs its national benchmark — factual above/below
					    context. Neutral tone: the ↑/↓ arrow carries the direction, no color judgment. */}
					{showBenchmark && nationalBenchmark !== undefined && (
						<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mt: 0.25 }} data-testid={`pbj-model-category-${category.id}-benchmark`}>
							{atOrAboveNational ? (
								<TrendingUpIcon sx={{ fontSize: 13, color: "text.secondary" }} />
							) : (
								<TrendingDownIcon sx={{ fontSize: 13, color: "text.secondary" }} />
							)}
							<Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
								{t("pbj_model.scoring.benchmark.national", { value: nationalBenchmark.toFixed(2) })}
							</Typography>
						</Stack>
					)}
				</TableCell>
				<TableCell sx={{ py: 1, px: 2, width: 0, display: "none", [BREAKDOWN_METER_QUERY]: { display: "table-cell", width: 130 } }}>
					{!naExcluded && hasScoreScale && (
						<SystemLinearMeter
							value={category.score}
							max={category.maxScore}
							color={meterColor}
							trackColor={TRACK_BG}
							ariaLabel={t("pbj_model.prediction.meter.aria_label", { title: category.title, score: category.score, max: category.maxScore })}
							dataTestId={`pbj-model-category-${category.id}-meter`}
						/>
					)}
				</TableCell>
				<TableCell align="right" sx={{ py: 1, px: 2, width: 104, [BREAKDOWN_NARROW_QUERY]: { px: 1 } }}>
					{naExcluded ? (
						<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
							{t("pbj_model.scoring.na.points")}
						</Typography>
					) : !hasScoreScale ? (
						// Available but no points scale — a muted dash reads as "nothing to score"
						// instead of a misleading "0 / 0".
						<Typography variant="body2" color="text.disabled" sx={{ fontWeight: 700 }} aria-label={t("pbj_model.scoring.no_scale")}>
							—
						</Typography>
					) : (
						<Stack direction="row" sx={{ alignItems: "baseline", gap: 0.5, justifyContent: "flex-end" }}>
							<Typography variant="body2" sx={{ fontWeight: "bold", color: accent, fontVariantNumeric: "tabular-nums" }}>
								{category.score}
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
								/ {category.maxScore}
							</Typography>
						</Stack>
					)}
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell sx={{ pb: 0, pt: 0 }} colSpan={4}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<CategoryExpandContent
							category={category}
							editable={editable}
							naExcluded={naExcluded}
							hasScoreScale={hasScoreScale}
							improvement={improvement}
							bandRangeBufferPct={bandRangeBufferPct}
							onEdit={onEdit}
						/>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

export const CategoryBreakdownTable: FC<Props> = ({ prediction, draftControls }) => {
	const { draft, onDraftChange, onCalculate } = draftControls;
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const tint = brandTint(theme);
	const border = brandBorder(theme);
	// `census` (passed to children) stays bound to the scorer's resolved value so
	// the HPPD-to-hours math matches the scores rendered in the table. `displayedCensus`
	// surfaces the pending draft override the moment the user saves the dialog — without
	// it, the chip would keep showing the old value until the next Calculate roundtrip.
	const census = prediction.resolvedAverageCensus;
	const displayedCensus = draft.averageCensus ?? census;
	const [censusOpen, setCensusOpen] = useState(false);
	// Read from the CONFIG the prediction was scored with, not the draft: this chip explains the
	// point values already on screen, so it must never advertise a buffer the scores don't carry
	// (the draft can hold an edited buffer until the next Calculate).
	const bandRangeBufferPct = prediction.config.bandRangeBufferPct;

	// Manual turnover override / N/A editor. `editCategory` gates the dialog open; the
	// dialog seeds itself from the live draft and writes back on save (draft + recompute).
	const [editCategory, setEditCategory] = useState<PbjCategoryScore | null>(null);

	return (
		<>
			{/* Natural flow (header → table). The card no longer force-fills its row: the always-on
			    per-measure descriptions give the breakdown real height, so it sits close to the How
			    column on its own and the Why cell drops the stretch (any small remaining gap is honest
			    breathing room, not padding). `containerType` is retained so the meter-column density
			    fix keys off the card's own width. */}
			<Box sx={{ containerType: "inline-size", containerName: BREAKDOWN_CONTAINER }}>
				<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1.5, minHeight: 32 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
						{t("pbj_model.scoring.breakdown_title")}
					</Typography>
					<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, justifyContent: "flex-end" }}>
						{/* Every point value below was scored against buffer-tightened thresholds — say so
						    where the scores are, not only inside the Safety Margin dialog that set it. */}
						{bandRangeBufferPct > 0 && (
							<MetaChip
								label={t("pbj_model.scoring.buffer.header_chip", { pct: bandRangeBufferPct })}
								icon={<ShieldOutlinedIcon />}
								tone="info"
								tooltip={t("pbj_model.scoring.buffer.header_chip_tooltip", { pct: bandRangeBufferPct })}
								size="medium"
								dataTestId="pbj-model-band-buffer-chip"
							/>
						)}
						<Chip
							icon={<SettingsIcon sx={{ fontSize: 12 }} />}
							label={t("pbj_model.scoring.census_chip", { count: displayedCensus })}
							size="small"
							onClick={() => setCensusOpen(true)}
							sx={{
								height: BADGE.TAG_CHIP_HEIGHT_MD,
								fontSize: FONT_SIZE.LABEL_XS,
								bgcolor: tint,
								color: accent,
								border: "1px solid",
								borderColor: border,
								fontWeight: 600,
								cursor: "pointer",
							}}
							data-testid="pbj-model-census-chip"
						/>
					</Stack>
				</Stack>
				{/* Documented exception to the "tables → SystemDataGrid" rule: this is a fixed ~6-row bespoke
			    diagnostic with a nested, expandable threshold ladder per row — not a data grid. SystemDataGrid's
			    search/pagination/virtualization add nothing here, and its row model can't host the two-column
			    expand panel cleanly. Sanctioned by the UI council review; keep as a hand-built <Table>. */}
				<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ bgcolor: TABLE_HEAD_BG }}>
								<TableCell sx={{ py: 0.5, pl: 1, pr: 0, width: 36 }} />
								<TableCell
									sx={{
										fontWeight: 700,
										color: "text.secondary",
										fontSize: FONT_SIZE.LABEL_XXS,
										textTransform: "uppercase",
										letterSpacing: LETTER_SPACING.LABEL,
										py: 0.5,
										pl: 0.5,
										pr: 2,
										[BREAKDOWN_NARROW_QUERY]: { pr: 1 },
									}}
								>
									{t("pbj_model.scoring.column.metric")}
								</TableCell>
								{/* Meter column — dropped when the card is too narrow to fit it without clipping
								    the Points cell (keyed on the card's width, not the viewport). */}
								<TableCell sx={{ py: 0.5, px: 2, width: 0, display: "none", [BREAKDOWN_METER_QUERY]: { display: "table-cell", width: 130 } }} />
								<TableCell
									align="right"
									sx={{
										fontWeight: 700,
										color: "text.secondary",
										fontSize: FONT_SIZE.LABEL_XXS,
										textTransform: "uppercase",
										letterSpacing: LETTER_SPACING.LABEL,
										py: 0.5,
										px: 2,
										width: 104,
										[BREAKDOWN_NARROW_QUERY]: { px: 1 },
									}}
								>
									{t("pbj_model.scoring.column.points")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{prediction.categories.map((category) => (
								<CategoryTableRow
									key={category.id}
									category={category}
									census={census}
									targetStarRating={prediction.targetStars}
									bandRangeBufferPct={bandRangeBufferPct}
									onEdit={setEditCategory}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>

			<CensusDialog
				open={censusOpen}
				onClose={() => setCensusOpen(false)}
				initialCensus={displayedCensus}
				onSave={(nextCensus) => onDraftChange({ ...draft, averageCensus: nextCensus })}
			/>

			<ManualTurnoverDialog
				category={editCategory}
				draft={draft}
				onClose={() => setEditCategory(null)}
				onDraftChange={onDraftChange}
				onCalculate={onCalculate}
			/>
		</>
	);
};
